/**
 * A PLAIN-ENGLISH WARNING BEFORE ANYTHING REBUILDS A CATEGORY'S RESEARCH.
 *
 * ⛔ THE GAP THIS CLOSES. Today these buttons run immediately. "Re-Research All" and every
 * per-aspect refresh REPLACE what is stored — including the buyer personas and any weighting a
 * person tuned by hand — and the operator gets no warning, no count, and no way back. The result
 * looks perfectly valid afterwards, so nothing tells them anything was lost.
 *
 * ⚠️ SHARED BY BOTH SURFACES ON PURPOSE. The personas render on two pages —
 * /admin/category-research (the one in the sidebar) and /research/category-perceptions — and a
 * warning added to one of them is a warning the other page's operator never sees.
 *
 * ⛔ NEVER RENDER A COUNT WE DO NOT HAVE. "No personas" and "not loaded yet" are the same empty
 * list, and a dialog that says "0 personas" about a category that simply has not loaded is telling
 * the operator there is nothing to lose at the moment they most need the opposite.
 */
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

/** Human labels for the aspect names the API uses. No field names reach the operator. */
const ASPECT_LABELS: Record<string, string> = {
    personas: 'buyer personas',
    price_tiers: 'price tiers',
    brand_tiers: 'brand tiers',
    product_types: 'product types',
    use_cases: 'use cases',
    premium_features: 'premium features',
    tier_vocabulary: 'tier vocabulary',
    spec_semantics: 'spec meanings',
    measurement_units: 'measurement units',
    validation_rules: 'validation rules',
};

export const aspectLabel = (aspect: string): string =>
    ASPECT_LABELS[aspect] || aspect.replace(/_/g, ' ');

export function RefreshConfirmDialog({
    open,
    onOpenChange,
    scope,
    categoryPath,
    sectorId,
    personaCount,
    categoriesAffected,
    batchSteps,
    batchTouchesPersonas,
    loading = false,
    onConfirm,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** 'all' rebuilds every researched aspect; otherwise the single aspect being rebuilt. */
    scope: 'all' | string;
    categoryPath: string;
    sectorId: number | undefined;
    /**
     * How many buyer personas are stored, or NULL when that is not known — not loaded yet, or a
     * read that failed. Null prints "we cannot tell you how many", never "0".
     */
    personaCount: number | null;
    /**
     * ⛔ BATCH MODE. When a run rebuilds MANY categories at once the per-category copy understates
     * it badly — "this category" beside a button that replaces forty of them is the wrong warning.
     * Supply the affected category paths and the dialog speaks about the set instead.
     */
    categoriesAffected?: string[];
    /**
     * ⛔⛔ WHAT THE BATCH RUN ACTUALLY REPLACES — the human labels of the selected steps that
     * overwrite. Without this the batch dialog claimed it replaced EVERYTHING, buyer personas
     * included, on a run whose only destructive step was perceived benefits.
     * 🔑 OVERSTATING THE BLAST RADIUS ERODES A WARNING EXACTLY AS FAST AS UNDERSTATING IT. The
     * "0 personas" case taught the operator the dialog is noise by claiming too little; this
     * teaches the same lesson by claiming too much, on the path where they most need to read it.
     */
    batchSteps?: string[];
    /**
     * Whether the selected steps actually rebuild the buyer personas. Explicit rather than inferred:
     * personas come from the characteristics step, and a run without it does not touch them.
     */
    batchTouchesPersonas?: boolean;
    loading?: boolean;
    onConfirm: () => void;
}) {
    const batch = categoriesAffected ?? null;
    const isBatch = batch !== null && batch.length > 0;
    const isAll = scope === 'all';
    const steps = batchSteps && batchSteps.length > 0 ? batchSteps : null;
    const stepList = steps
        ? steps.length === 1
            ? steps[0]
            : `${steps.slice(0, -1).join(', ')} and ${steps[steps.length - 1]}`
        : null;
    const what = isBatch
        ? stepList
            ? `the ${stepList} on ${batch.length} categor${batch.length === 1 ? 'y' : 'ies'}`
            : `everything researched for ${batch.length} categor${batch.length === 1 ? 'y' : 'ies'}`
        : isAll ? 'everything researched for this category' : `the ${aspectLabel(scope)}`;
    /**
     * ⚠️ STEP-AWARE MUST NOT BECOME STEP-OPTIMISTIC. For a batch the caller states this explicitly,
     * and it says yes for any selected step whose overwrite behaviour is UNVERIFIED — narrowing the
     * claim is only honest where the narrowing itself was checked.
     */
    const touchesPersonas = isBatch ? !!batchTouchesPersonas : (isAll || scope === 'personas');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90dvh] flex-col overflow-hidden sm:max-w-[540px]">
                <div role="region" aria-label="Review research replacement" tabIndex={0}
                    className="min-h-0 flex-1 overflow-y-auto overscroll-contain space-y-4 pr-2">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                        </div>
                        <DialogTitle className="text-red-600">
                            This replaces {what}
                        </DialogTitle>
                    </div>
                    <DialogDescription className="pt-2">
                        {/* ⛔ BATCH IS NOT A BIGGER SINGULAR. "every part of this category" beside a
                            button that rebuilds forty of them understates the operation by a factor
                            of forty, and the title above already says "categories" — so the body
                            contradicted the heading on the most destructive control of the two. */}
                        Researching again builds{' '}
                        {isBatch
                            ? stepList
                                ? `the ${stepList} on all ${batch!.length} of them`
                                : `every part of all ${batch!.length} of them`
                            : isAll ? 'every part of this category' : `the ${aspectLabel(scope)}`}{' '}
                        from scratch. Whatever is stored now is replaced by whatever this run
                        produces, and <strong>this cannot be undone</strong>.
                        {touchesPersonas && isBatch ? (
                            <> That includes the buyer personas stored on each of them.</>
                        ) : touchesPersonas && (
                            <>
                                {' '}
                                {personaCount === null
                                    ? 'That includes the buyer personas — the current list has not loaded, so we cannot tell you how many are stored.'
                                    : personaCount === 0
                                        ? 'There are no buyer personas stored for this category yet.'
                                        : `That includes ${personaCount} buyer persona${personaCount === 1 ? '' : 's'}, and any benefit weighting tuned by hand on ${personaCount === 1 ? 'it' : 'them'}.`}
                            </>
                        )}
                        {' '}The new result will look perfectly valid, so nothing afterwards will
                        tell you what changed.
                    </DialogDescription>
                </DialogHeader>

                {/* ⛔ THE ROW BEING CHANGED, VERBATIM. The same category name exists under more than
                    one sector, so naming only the path is not enough to tell an operator whose data
                    they are about to overwrite. Shown untruncated: this is the row that gets written. */}
                <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                    <div className="flex gap-2">
                        <span className="text-muted-foreground w-16 shrink-0">Sector</span>
                        <span className="font-mono">{sectorId ?? '(not known — refusing to guess)'}</span>
                    </div>
                    <div className="flex gap-2">
                        <span className="text-muted-foreground w-16 shrink-0">
                            {isBatch ? 'Categories' : 'Category'}
                        </span>
                        {/* ⛔ NAME THEM. A count alone lets an operator confirm a set they never saw;
                            the list is capped so a large batch stays readable, and the remainder is
                            stated rather than silently dropped. */}
                        <span className="font-mono break-all">
                            {isBatch
                                ? `${batch!.slice(0, 8).join(', ')}${batch!.length > 8 ? ` — and ${batch!.length - 8} more` : ''}`
                                : categoryPath}
                        </span>
                    </div>
                </div>

                </div>
                <DialogFooter className="shrink-0 gap-2 border-t pt-4 sm:gap-0">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="destructive" onClick={() => { if (!loading) onConfirm(); }} disabled={loading}>
                        {loading
                            ? 'Researching...'
                            : isBatch
                                ? `Replace research for ${batch!.length} categor${batch!.length === 1 ? 'y' : 'ies'}`
                                : isAll ? 'Replace everything' : `Replace the ${aspectLabel(scope)}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
